import { Card, CardContent, Typography, Box, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, Legend } from 'recharts';

type PieChartCardProps = {
  title: string;
  data: Array<{ name: string; value: number }>;
  colors: string[];
  height?: number;
  showLegend?: boolean;
  showLabels?: boolean;
  innerRadius?: number | string;
  outerRadius?: number | string;
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: 12,
  boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.05)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  transition: 'all 0.3s cubic-bezier(.25,.8,.25,1)',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: theme.palette.primary.main,
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 8px 30px 0 rgba(0, 0, 0, 0.12)',
    borderColor: theme.palette.error.main,
    '&::before': {
      opacity: 1,
      background: theme.palette.error.main,
    },
  },
}));

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
  name,
}: any) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN) * 1.2;
  const y = cy + radius * Math.sin(-midAngle * RADIAN) * 1.2;

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      style={{
        fontSize: '12px',
        fontWeight: 'bold',
        textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
      }}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const PieChartCard = ({ 
  title, 
  data, 
  colors, 
  height = 300, 
  showLegend = false,
  showLabels = false,
  innerRadius = 0,
  outerRadius = '80%'
}: PieChartCardProps) => {
  const theme = useTheme();
  
  return (
    <StyledCard>
      <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ 
          flex: 1,
          minHeight: height,
          '& .recharts-legend-item-text': {
            color: `${theme.palette.text.primary} !important`,
            fontSize: '0.75rem',
          },
          '& .recharts-tooltip-wrapper': {
            backgroundColor: theme.palette.background.paper,
            borderRadius: '8px',
            boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.12)',
            border: 'none',
            padding: '8px',
          },
          '& .recharts-tooltip-label': {
            color: theme.palette.text.primary,
            fontWeight: 600,
            marginBottom: '4px',
          },
        }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={showLabels ? renderCustomizedLabel : undefined}
                outerRadius={outerRadius}
                innerRadius={innerRadius}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
                ))}
              </Pie>
              {showLegend && (
                <Legend 
                  layout="horizontal" 
                  verticalAlign="bottom" 
                  align="center"
                  wrapperStyle={{
                    paddingTop: '16px',
                  }}
                />
              )}
              <RechartsTooltip 
                formatter={(value, name) => [`${name} (${value})`, ' ']}
                itemSorter={(item) => -Number(item.value)}
                itemStyle={{
                  color: theme.palette.text.primary,
                  fontSize: '0.875rem',
                }}
                contentStyle={{
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '8px',
                  padding: '8px 12px',
                }}
                labelStyle={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  marginBottom: '4px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default PieChartCard;
